'use client';

import { minutesToDisplayTime } from '@/shared/utils/time';
import { useCallback, useRef } from 'react';
import { DEFAULT_MINIMUM_WINDOW } from '../constants';
import { TimeRangeSliderProps } from '../types';

const TimeRangeSlider = ({
  startTime,
  endTime,
  onChange,
  disabled = false,
  minimumWindow = DEFAULT_MINIMUM_WINDOW,
  className,
  dateDisplay,
}: TimeRangeSliderProps) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const startThumbRef = useRef<HTMLDivElement>(null);
  const endThumbRef = useRef<HTMLDivElement>(null);

  const minTime = 480; // 8:00 AM
  const maxTime = 1320; // 10:00 PM
  const stepSize = minimumWindow; // Use account setting for step size

  // Calculate percentages for positioning
  const startPercent = ((startTime - minTime) / (maxTime - minTime)) * 100;
  const endPercent = ((endTime - minTime) / (maxTime - minTime)) * 100;

  const handleMouseDown = useCallback(
    (isStart: boolean, e: React.MouseEvent) => {
      if (disabled) return;

      e.preventDefault();
      const slider = sliderRef.current;
      if (!slider) return;

      const rect = slider.getBoundingClientRect();
      const sliderWidth = rect.width;

      const handleMouseMove = (e: MouseEvent) => {
        const x = e.clientX - rect.left;
        const percent = Math.max(0, Math.min(100, (x / sliderWidth) * 100));
        const rawTime = (percent / 100) * (maxTime - minTime) + minTime;

        // Snap to step intervals
        const newTime = Math.round(rawTime / stepSize) * stepSize;
        const clampedTime = Math.max(minTime, Math.min(maxTime, newTime));

        if (isStart) {
          const maxStartTime = endTime - minimumWindow;
          const constrainedStartTime = Math.min(clampedTime, maxStartTime);
          if (constrainedStartTime !== startTime) {
            onChange(constrainedStartTime, endTime);
          }
        } else {
          const minEndTime = startTime + minimumWindow;
          const constrainedEndTime = Math.max(clampedTime, minEndTime);
          if (constrainedEndTime !== endTime) {
            onChange(startTime, constrainedEndTime);
          }
        }
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [disabled, startTime, endTime, onChange, minimumWindow, minTime, maxTime, stepSize]
  );

  // Handle keyboard events
  const handleKeyDown = useCallback(
    (isStart: boolean, e: React.KeyboardEvent) => {
      if (disabled) return;

      let newTime = isStart ? startTime : endTime;

      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowDown':
          newTime -= stepSize;
          break;
        case 'ArrowRight':
        case 'ArrowUp':
          newTime += stepSize;
          break;
        case 'Home':
          newTime = isStart ? minTime : startTime + minimumWindow;
          break;
        case 'End':
          newTime = isStart ? endTime - minimumWindow : maxTime;
          break;
        default:
          return;
      }

      e.preventDefault();

      if (isStart) {
        const constrainedTime = Math.max(
          minTime,
          Math.min(endTime - minimumWindow, newTime)
        );
        if (constrainedTime !== startTime) {
          onChange(constrainedTime, endTime);
        }
      } else {
        const constrainedTime = Math.max(
          startTime + minimumWindow,
          Math.min(maxTime, newTime)
        );
        if (constrainedTime !== endTime) {
          onChange(startTime, constrainedTime);
        }
      }
    },
    [disabled, startTime, endTime, onChange, minimumWindow, minTime, maxTime, stepSize]
  );

  return (
    <div className={`w-full ${className}`}>
      {/* Time Display - Left aligned */}
      <div className="mb-4">
        <div className="text-left">
          {dateDisplay && <span className="mr-3">{dateDisplay}</span>}
          {minutesToDisplayTime(startTime)} - {minutesToDisplayTime(endTime)}
        </div>
      </div>

      {/* Slider Container */}
      <div className="relative">
        {/* Track */}
        <div
          ref={sliderRef}
          className={`relative h-2 rounded-full bg-gray-200 ${disabled ? 'opacity-50' : 'cursor-pointer'}`}
          onClick={(e) => {
            if (disabled) return;

            const rect = e.currentTarget.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const percent = (x / rect.width) * 100;
            const rawTime = (percent / 100) * (maxTime - minTime) + minTime;

            // Snap to step intervals
            const clickTime = Math.round(rawTime / stepSize) * stepSize;
            const clampedTime = Math.max(minTime, Math.min(maxTime, clickTime));

            // Determine which thumb is closer and move that one
            const startDistance = Math.abs(clampedTime - startTime);
            const endDistance = Math.abs(clampedTime - endTime);

            if (startDistance < endDistance) {
              const maxStartTime = endTime - minimumWindow;
              const newStartTime = Math.max(minTime, Math.min(maxStartTime, clampedTime));
              onChange(newStartTime, endTime);
            } else {
              const minEndTime = startTime + minimumWindow;
              const newEndTime = Math.max(minEndTime, Math.min(maxTime, clampedTime));
              onChange(startTime, newEndTime);
            }
          }}>
          {/* Active Range */}
          <div
            className="bg-primary absolute h-full rounded-full"
            style={{
              left: `${startPercent}%`,
              right: `${100 - endPercent}%`,
            }}
          />

          {/* Start Thumb */}
          <div
            ref={startThumbRef}
            className={`bg-primary absolute top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 transform rounded-full border-2 border-white shadow-lg ${
              disabled ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'
            } focus:ring-primary focus:ring-2 focus:ring-offset-2 focus:outline-none`}
            style={{ left: `${startPercent}%` }}
            tabIndex={disabled ? -1 : 0}
            role="slider"
            aria-valuemin={minTime}
            aria-valuemax={endTime - minimumWindow}
            aria-valuenow={startTime}
            aria-label="Start time"
            onMouseDown={(e) => handleMouseDown(true, e)}
            onKeyDown={(e) => handleKeyDown(true, e)}
          />

          {/* End Thumb */}
          <div
            ref={endThumbRef}
            className={`bg-primary absolute top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 transform rounded-full border-2 border-white shadow-lg ${
              disabled ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'
            } focus:ring-primary focus:ring-2 focus:ring-offset-2 focus:outline-none`}
            style={{ left: `${endPercent}%` }}
            tabIndex={disabled ? -1 : 0}
            role="slider"
            aria-valuemin={startTime + minimumWindow}
            aria-valuemax={maxTime}
            aria-valuenow={endTime}
            aria-label="End time"
            onMouseDown={(e) => handleMouseDown(false, e)}
            onKeyDown={(e) => handleKeyDown(false, e)}
          />
        </div>

        {/* Time Labels */}
        <div className="mt-2 flex justify-between text-xs text-gray-500">
          <span>8:00 AM</span>
          <span>10:00 PM</span>
        </div>
      </div>
    </div>
  );
};

export default TimeRangeSlider;
